import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Shield, 
  Search
} from 'lucide-react';
import PresetForm from './presets/PresetForm';
import PresetPermissions from './presets/PresetPermissions';
import permissionPresetService from 'features/administrativo/services/permissionPresetService';
import NotificationToast from 'components/common/NotificationToast';

const PresetManagement = () => {
  // Estados
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPresets, setFilteredPresets] = useState([]);
  const [notification, setNotification] = useState(null);

  // Carregar presets
  const loadPresets = useCallback(async () => {
    try {
      setLoading(true);
      const response = await permissionPresetService.getAll();
      setPresets(response.data);
      setFilteredPresets(response.data);
    } catch (error) {
      console.error('Erro ao carregar presets:', error);
      showNotification('Erro ao carregar presets', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Filtrar presets
  const filteredPresetsMemo = useMemo(() => {
    if (searchTerm) {
      return presets.filter(preset => 
        (preset.name && String(preset.name).toLowerCase().includes(searchTerm.toLowerCase())) ||
        (preset.label && String(preset.label).toLowerCase().includes(searchTerm.toLowerCase())) ||
        (preset.description && String(preset.description).toLowerCase().includes(searchTerm.toLowerCase()))
      );
    } else {
      return presets;
    }
  }, [searchTerm, presets]);

  useEffect(() => {
    setFilteredPresets(filteredPresetsMemo);
  }, [filteredPresetsMemo]);

  // Carregar dados iniciais
  useEffect(() => {
    loadPresets();
  }, [loadPresets]);

  // Notificações
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Handlers
  const handleCreatePreset = () => {
    setSelectedPreset(null);
    setShowForm(true);
  };

  const handleEditPreset = (preset) => {
    setSelectedPreset(preset);
    setShowForm(true);
  };

  const handleViewPermissions = (preset) => {
    setSelectedPreset(preset);
    setShowPermissions(true);
  };

  const handleDeletePreset = async (preset) => {
    if (window.confirm(`Tem certeza que deseja excluir o preset "${preset.label}"?`)) {
      try {
        await permissionPresetService.delete(preset.id);
        showNotification('Preset excluído com sucesso!', 'success');
        loadPresets();
      } catch (error) {
        console.error('Erro ao excluir preset:', error);
        showNotification('Erro ao excluir preset', 'error');
      }
    }
  };

  const handleFormSubmit = async (presetData) => {
    try {
      if (selectedPreset) {
        await permissionPresetService.update(selectedPreset.id, presetData);
        showNotification('Preset atualizado com sucesso!', 'success');
      } else {
        await permissionPresetService.create(presetData);
        showNotification('Preset criado com sucesso!', 'success');
      }
      setShowForm(false);
      loadPresets();
    } catch (error) {
      console.error('Erro ao salvar preset:', error);
      showNotification('Erro ao salvar preset', 'error');
    }
  };

  const handlePermissionsUpdate = async (permissions) => {
    try {
      await permissionPresetService.updatePermissions(selectedPreset.id, permissions);
      showNotification('Permissões atualizadas com sucesso!', 'success');
      setShowPermissions(false);
      loadPresets();
    } catch (error) {
      console.error('Erro ao atualizar permissões:', error);
      showNotification('Erro ao atualizar permissões', 'error');
    }
  };

  // Renderizar card de preset
  const renderPresetCard = (preset) => (
    <div key={preset.id} className="rounded-lg border p-6 theme-surface hover:opacity-95 transition-opacity">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Shield className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold theme-text">{preset.label}</h3>
              <p className="text-sm theme-text-muted">{preset.description || 'Sem descrição'}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4 text-sm theme-text-muted mb-4">
            <div className="flex items-center gap-1">
              <Shield className="h-4 w-4" />
              <span>{preset.permissions?.length || 0} permissões</span>
            </div>
          </div>

          {preset.permissions && preset.permissions.length > 0 && (
            <div className="mb-4">
              <p className="text-xs theme-text-muted mb-2">Permissões principais:</p>
              <div className="flex flex-wrap gap-1">
                {preset.permissions.slice(0, 3).map(permission => (
                  <span 
                    key={permission}
                    className="px-2 py-1 text-xs rounded-full theme-surface-muted"
                  >
                    {permission}
                  </span>
                ))}
                {preset.permissions.length > 3 && (
                  <span className="px-2 py-1 text-xs rounded-full theme-surface-muted">
                    +{preset.permissions.length - 3} mais
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => handleViewPermissions(preset)}
            className="p-2 theme-text-muted hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Gerenciar Permissões"
          >
            <Shield className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleEditPreset(preset)}
            className="p-2 theme-text-muted hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            title="Editar Preset"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDeletePreset(preset)}
            className="p-2 theme-text-muted hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Excluir Preset"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
          <h3 className="text-lg font-medium mb-2 theme-text">Carregando presets...</h3>
          <p className="text-sm theme-text-muted">Aguarde enquanto carregamos os dados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold theme-text">Gerenciar Presets de Permissões</h1>
          <p className="theme-text-muted">Gerencie presets de permissões (cargos) no sistema</p>
        </div>
        <button
          onClick={handleCreatePreset}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Novo Preset
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 theme-text-muted" />
          <input
            type="text"
            placeholder="Buscar por nome ou descrição..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent theme-surface"
            style={{ borderColor: 'var(--border)' }}
          />
        </div>
      </div>

      {/* Lista de Presets */}
      {filteredPresets.length === 0 ? (
        <div className="text-center py-12">
          <Shield className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium theme-text">Nenhum preset encontrado</h3>
          <p className="mt-1 text-sm theme-text-muted">
            {searchTerm ? 'Tente ajustar os filtros de busca.' : 'Comece criando um novo preset.'}
          </p>
          {!searchTerm && (
            <div className="mt-6">
              <button
                onClick={handleCreatePreset}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Criar Primeiro Preset
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredPresets.map(renderPresetCard)}
        </div>
      )}

      {/* Modais */}
      {showForm && (
        <PresetForm
          preset={selectedPreset}
          onSubmit={handleFormSubmit}
          onClose={() => setShowForm(false)}
        />
      )}

      {showPermissions && selectedPreset && (
        <PresetPermissions
          preset={selectedPreset}
          onUpdate={handlePermissionsUpdate}
          onClose={() => setShowPermissions(false)}
        />
      )}

      {/* Notificações */}
      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

export default PresetManagement;
